// src/components/WorkflowCreator/WorkflowBuilder.jsx
// Editor visual de workflows con drag & drop

import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  MiniMap,
  ConnectionLineType,
  MarkerType,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import './WorkflowBuilder.css';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Save,
  Play,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  AlertTriangle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { nodeTypes } from './nodes/CustomNodes';
import { NODE_DEFINITIONS, createNodeInstance } from './nodes/nodeDefinitions';
import NodePalette from './panels/NodePalette';
import NodeConfigPanel from './panels/NodeConfigPanel';
import WorkflowService from '../../services/workflow.service';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useAuth } from '../../useAuth';
import ConfirmDialog from '../ui/ConfirmDialog';

// Simple UUID generator (crypto.randomUUID fallback)
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Edge styles
const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  style: {
    stroke: '#6366f1',
    strokeWidth: 2,
  },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#6366f1',
  },
};

/**
 * Workflow Builder Component
 */
const WorkflowBuilderInner = ({ 
  workflowId = null, // null = nuevo workflow
  projectId = null,  // ID del proyecto actual
  initialData = null,
  onSave,
  onBack
}) => {
  const navigate = useNavigate();
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  
  // Panel states
  const [isPaletteOpen, setIsPaletteOpen] = useState(true);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  
  // Workflow metadata
  const [workflowName, setWorkflowName] = useState(initialData?.name || 'New Workflow');
  const [workflowDescription, setWorkflowDescription] = useState(initialData?.description || '');
  const [workflowTags, setWorkflowTags] = useState(initialData?.tags || []);
  
  // Temporary workflow ID (UUID until saved)
  const [tempWorkflowId] = useState(() => workflowId || generateUUID());
  
  // ReactFlow states
  const [nodes, setNodes, onNodesChange] = useNodesState(initialData?.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialData?.edges || []);
  
  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // Context
  const { currentAccount } = useOrganization();
  const { user } = useAuth();

  // Update nodes/edges when initialData changes (for loading existing workflows)
  useEffect(() => {
    if (initialData) {
      if (initialData.nodes && initialData.nodes.length > 0) {
        setNodes(initialData.nodes);
      }
      if (initialData.edges) {
        setEdges(initialData.edges);
      }
      if (initialData.name) {
        setWorkflowName(initialData.name);
      }
      if (initialData.description) {
        setWorkflowDescription(initialData.description);
      }
      if (initialData.tags) {
        setWorkflowTags(initialData.tags);
      }
      // Reset unsaved changes flag after loading
      setHasUnsavedChanges(false);
    }
  }, [initialData, setNodes, setEdges]);

  // Mark as having unsaved changes when nodes/edges change
  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [nodes, edges, workflowName, workflowDescription]);

  // Handle node selection
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setIsConfigOpen(true);
  }, []);

  // Handle connection between nodes
  const onConnect = useCallback(
    (params) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            ...defaultEdgeOptions,
          },
          eds
        )
      );
    },
    [setEdges]
  );

  // Handle drag over canvas
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop on canvas
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const nodeType = event.dataTransfer.getData('application/reactflow');
      if (!nodeType || !NODE_DEFINITIONS[nodeType]) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = createNodeInstance(nodeType, position);
      
      // Add workflow_id reference
      newNode.data.workflowId = tempWorkflowId;

      setNodes((nds) => nds.concat(newNode));
      
      // Auto-select the new node
      setSelectedNode(newNode);
      setIsConfigOpen(true);
    },
    [reactFlowInstance, setNodes, tempWorkflowId]
  );

  // Handle node configuration save
  const onNodeConfigSave = useCallback(({ nodeId, params, stepName, stepDescription, isConfigured }) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              params,
              stepName,
              stepDescription,
              isConfigured
            }
          };
        }
        return node;
      })
    );
  }, [setNodes]);

  // Handle node deletion
  const onNodeDelete = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    setSelectedNode(null);
    setIsConfigOpen(false);
  }, [setNodes, setEdges]);

  // Build workflow data for API
  const buildWorkflowPayload = useCallback(() => {
    // Find trigger nodes
    const triggerNodes = nodes.filter(n => NODE_DEFINITIONS[n.data.type]?.isTrigger);
    const stepNodes = nodes.filter(n => !NODE_DEFINITIONS[n.data.type]?.isTrigger);

    // Build triggers array
    const triggers = triggerNodes.map(node => {
      const definition = NODE_DEFINITIONS[node.data.type];
      return definition.generateTriggerConfig(node.data.params || {});
    });

    // Build steps array with proper order based on connections
    const orderedSteps = [];
    const nodeMap = new Map(stepNodes.map(n => [n.id, n]));
    const processedNodes = new Set();
    
    // Find root steps (connected directly from triggers)
    const rootStepIds = edges
      .filter(e => triggerNodes.some(t => t.id === e.source))
      .map(e => e.target);

    // BFS to order steps
    const queue = [...rootStepIds];
    let stepOrder = 0;

    while (queue.length > 0) {
      const nodeId = queue.shift();
      if (processedNodes.has(nodeId) || !nodeMap.has(nodeId)) continue;

      const node = nodeMap.get(nodeId);
      processedNodes.add(nodeId);

      const definition = NODE_DEFINITIONS[node.data.type];
      if (!definition) continue;

      // Find dependencies (nodes that connect TO this node)
      const dependencies = edges
        .filter(e => e.target === nodeId)
        .map(e => e.source)
        .filter(id => !triggerNodes.some(t => t.id === id));

      // Generate step parameters
      const stepParams = definition.generateStepParams
        ? definition.generateStepParams(node.data.params || {})
        : { step_type: node.data.type, ...node.data.params };

      orderedSteps.push({
        id: node.id,
        workflow_id: tempWorkflowId,
        name: node.data.stepName || `Step ${stepOrder + 1}`,
        description: node.data.stepDescription || definition.description,
        step_order: stepOrder,
        parameters: stepParams,
        dependencies: dependencies,
        timeout_seconds: 300,
        conditions: null,
        parallel_group: null,
        is_critical: true,
        step_metadata: {}
      });

      stepOrder++;

      // Add connected nodes to queue
      const nextNodes = edges
        .filter(e => e.source === nodeId)
        .map(e => e.target);
      queue.push(...nextNodes);
    }

    return {
      name: workflowName,
      description: workflowDescription,
      tags: workflowTags,
      version: '1.0.0',
      timeout_seconds: 300,
      triggers,
      steps: orderedSteps,
      // ReactFlow state for UI (optional, for reloading)
      _ui_state: {
        nodes: nodes.map(n => ({
          id: n.id,
          type: n.type,
          position: n.position,
          data: n.data
        })),
        edges
      }
    };
  }, [nodes, edges, workflowName, workflowDescription, workflowTags, tempWorkflowId]);

  // Validate workflow before saving
  const validateWorkflow = useCallback(() => {
    const errors = [];

    // Must have at least one trigger
    const triggerNodes = nodes.filter(n => NODE_DEFINITIONS[n.data.type]?.isTrigger);
    if (triggerNodes.length === 0) {
      errors.push('Workflow must have at least one trigger');
    }

    // Must have at least one step
    const stepNodes = nodes.filter(n => !NODE_DEFINITIONS[n.data.type]?.isTrigger);
    if (stepNodes.length === 0) {
      errors.push('Workflow must have at least one step');
    }

    // All nodes must be configured
    const unconfiguredNodes = nodes.filter(n => !n.data.isConfigured);
    if (unconfiguredNodes.length > 0) {
      errors.push(`${unconfiguredNodes.length} node(s) are not configured`);
    }

    // Workflow must have a name
    if (!workflowName.trim()) {
      errors.push('Workflow name is required');
    }

    return errors;
  }, [nodes, workflowName]);

  // Save workflow
  const handleSave = useCallback(async () => {
    const validationErrors = validateWorkflow();
    if (validationErrors.length > 0) {
      setSaveError(validationErrors.join('. '));
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const payload = buildWorkflowPayload();
      
      // Add account and project info
      payload.account_id = currentAccount?.id;
      // Usar projectId prop primero, luego default_project_id del account
      payload.project_id = projectId || currentAccount?.default_project_id;
      
      if (!payload.project_id) {
        setSaveError('No project selected. Please select a project before saving.');
        setIsSaving(false);
        return;
      }

      let result;
      if (workflowId) {
        // Update existing workflow - agregar workflow_id y user_id
        payload.workflow_id = workflowId;
        payload.user_id = user?.user_id || user?.sub;
        
        console.log('Updating workflow with:', { workflow_id: workflowId, user_id: payload.user_id });
        result = await WorkflowService.updateWorkflow(workflowId, payload);
      } else {
        // Create new workflow
        result = await WorkflowService.createWorkflow(payload);
      }

      if (result.success) {
        setHasUnsavedChanges(false);
        if (onSave) {
          onSave(result.data.workflow);
        }
      } else {
        setSaveError(result.error || 'Failed to save workflow');
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
      setSaveError(error.message || 'Failed to save workflow');
    } finally {
      setIsSaving(false);
    }
  }, [validateWorkflow, buildWorkflowPayload, workflowId, currentAccount, onSave]);

  // Handle back button
  const handleBack = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
      return;
    }
    navigateBack();
  }, [hasUnsavedChanges]);

  // Navigate back to the correct route
  const navigateBack = useCallback(() => {
    if (onBack) {
      onBack();
    } else if (projectId) {
      navigate(`/dashboard?view=Projects&projectId=${projectId}&tab=workflows`);
    } else {
      navigate('/dashboard');
    }
  }, [onBack, navigate, projectId]);

  // Handle confirm leave
  const handleConfirmLeave = useCallback(() => {
    setShowUnsavedDialog(false);
    navigateBack();
  }, [navigateBack]);

  // Handle cancel leave
  const handleCancelLeave = useCallback(() => {
    setShowUnsavedDialog(false);
  }, []);

  // Validation errors for display
  const validationErrors = useMemo(() => validateWorkflow(), [validateWorkflow]);

  return (
    <div className="h-screen w-full flex flex-col bg-[#0a0a0a]">
      {/* Top Bar */}
      <div className="h-14 border-b border-white/10 flex items-center justify-between px-4 bg-[#0d0d0d]">
        {/* Left side */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-400" />
          </button>
          
          <div className="h-6 w-px bg-white/10" />
          
          <input
            type="text"
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
            placeholder="Workflow name..."
            className="bg-transparent text-white font-semibold text-lg focus:outline-none placeholder-gray-500 min-w-[200px]"
          />
          
          {hasUnsavedChanges && (
            <span className="text-xs text-amber-400 flex items-center gap-1">
              <div className="w-1.5 h-1.5 bg-amber-400 rounded-full" />
              Unsaved changes
            </span>
          )}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Validation status */}
          {validationErrors.length > 0 ? (
            <div className="flex items-center gap-1 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-xs">{validationErrors.length} issue(s)</span>
            </div>
          ) : nodes.length > 0 && (
            <div className="flex items-center gap-1 px-3 py-1 bg-green-500/10 border border-green-500/20 rounded-lg">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="text-green-400 text-xs">Ready to save</span>
            </div>
          )}

          {/* Action buttons */}
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm bg-gray-700 hover:bg-gray-600 text-white transition-all duration-200"
          >
            Test
          </button>

          <button
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm bg-gray-700 hover:bg-gray-600 text-white transition-all duration-200"
          >
            Run
          </button>

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={isSaving || validationErrors.length > 0}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm
              transition-all duration-200
              ${validationErrors.length === 0 && !isSaving
                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Workflow
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error banner */}
      <AnimatePresence>
        {saveError && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="bg-red-500/10 border-b border-red-500/20"
          >
            <div className="px-4 py-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-red-400 text-sm">{saveError}</span>
              <button
                onClick={() => setSaveError(null)}
                className="ml-auto text-red-400 hover:text-red-300 text-sm"
              >
                Dismiss
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Node Palette */}
        <AnimatePresence>
          {isPaletteOpen && (
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: 280 }}
              exit={{ width: 0 }}
              className="overflow-hidden flex-shrink-0"
            >
              <NodePalette isOpen={isPaletteOpen} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Canvas */}
        <div className="flex-1 relative" ref={reactFlowWrapper}>
          {/* Toggle palette button */}
          <button
            onClick={() => setIsPaletteOpen(!isPaletteOpen)}
            className="absolute top-4 left-4 z-10 p-2 bg-[#1a1a2e] border border-white/10 rounded-lg hover:bg-white/10 transition-colors"
          >
            {isPaletteOpen ? (
              <PanelLeftClose className="w-4 h-4 text-gray-400" />
            ) : (
              <PanelLeftOpen className="w-4 h-4 text-gray-400" />
            )}
          </button>

          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onInit={setReactFlowInstance}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            connectionLineType={ConnectionLineType.SmoothStep}
            connectionLineStyle={{ stroke: '#6366f1', strokeWidth: 2 }}
            fitView
            snapToGrid
            snapGrid={[15, 15]}
            deleteKeyCode="Delete"
            edgesFocusable={true}
            edgesUpdatable={true}
            className="bg-[#0a0a0a]"
          >
            <Background
              color="#1a1a2e"
              gap={20}
              size={1}
            />
            <Controls
              className="!bg-[#1a1a2e] !border-white/10 !rounded-lg"
              showInteractive={false}
            />
            <MiniMap
              className="!bg-[#1a1a2e] !border-white/10 !rounded-lg"
              nodeColor={(node) => {
                const def = NODE_DEFINITIONS[node.data?.type];
                return def?.isTrigger ? '#22c55e' : '#6366f1';
              }}
              maskColor="rgba(0, 0, 0, 0.8)"
            />

            {/* Empty state */}
            {nodes.length === 0 && (
              <Panel position="top-center" className="mt-20">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500/20 to-indigo-500/20 border border-white/10 flex items-center justify-center">
                    <Play className="w-8 h-8 text-purple-400" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">
                    Start building your workflow
                  </h3>
                  <p className="text-gray-400 text-sm max-w-md">
                    Drag nodes from the left panel to the canvas. Start with a trigger,
                    then add steps to process your data.
                  </p>
                </div>
              </Panel>
            )}
          </ReactFlow>
        </div>

        {/* Right Panel - Node Config */}
        <AnimatePresence>
          {isConfigOpen && selectedNode && (
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: 360 }}
              exit={{ width: 0 }}
              className="overflow-hidden flex-shrink-0"
            >
              <NodeConfigPanel
                selectedNode={selectedNode}
                isOpen={isConfigOpen}
                onClose={() => {
                  setIsConfigOpen(false);
                  setSelectedNode(null);
                }}
                onSave={onNodeConfigSave}
                onDelete={onNodeDelete}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Unsaved Changes Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showUnsavedDialog}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave? All your changes will be lost."
        confirmText="Leave"
        cancelText="Stay"
        variant="warning"
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
      />
    </div>
  );
};

/**
 * Wrapper with ReactFlowProvider
 */
const WorkflowBuilder = (props) => {
  return (
    <ReactFlowProvider>
      <WorkflowBuilderInner {...props} />
    </ReactFlowProvider>
  );
};

export default WorkflowBuilder;
